import { Common, CustomScrollbar, Icon } from "@/components";
import { useImportOrder } from "../providers/ImportOrderContext";
import { useNavigate } from "react-router-dom";
import { ErrorData } from "../models/import";
import ReactTooltip from "react-tooltip";
import { useEffect } from "react";
import { sortFieldsByConfig } from "../utils";

const ImportResult = () => {
  const { state, handleCancel, dispatch, refetchMappings } = useImportOrder();
  const navigate = useNavigate();

  const handleRetry = () => {
    dispatch({ type: "RESET" });
    navigate(".");
    refetchMappings();
  };

  const handleBackToList = () => {
    handleCancel({ mode: "cancel" });
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [state.errorDetails]);

  return (
    <div className="relative">
      <div className="h-[calc(100vh-258px)]">
        {state.importSuccess ? (
          <div className="text-center flex items-center justify-center flex-col">
            <div className="mb-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <Icon.CheckedCircle size="40" className="fill-green-500" />
              </div>
            </div>
            <div className="text-2xl font-bold mb-2">Import Successful</div>
            <div className="text-neutral-50 mb-6">
              Successfully imported {state.recordsImported} records.
            </div>
          </div>
        ) : (
          <>
            {state.errors && state.errors.length > 0 && (
              <div className="text-neutral-50 mb-6">
                <div className="text-center">
                  <div className="mb-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                      <Icon.WarningCircle size="40" className="fill-red-500" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold mb-2">Import Failed</div>
                  <ul className="list-disc pl-5 text-left">
                    {state.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {state.errorDetails && state.errorDetails.length > 0 && (
              <div className="h-[calc(100%-80px)]">
                <CustomScrollbar autoHide>
                  <table className="w-full border border-line border-separate border-spacing-0">
                    <thead className="sticky top-0 z-[1]">
                      <tr>
                        <th className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left">
                          Line
                        </th>
                        {Object.keys(state.mappingFields)
                          .sort((a, b) =>
                            sortFieldsByConfig(a, b, state.fieldConfigs)
                          )
                          .map((key) => (
                            <th
                              key={key}
                              className="px-4 py-2 bg-neutral-5 text-neutral-60 font-medium border-b border-neutral-10 text-left whitespace-nowrap"
                            >
                              {state.mappingFields[key]}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {state.errorDetails.map(
                        (errorData: ErrorData, rowIndex: number) => (
                          <tr key={rowIndex} className="hover:bg-neutral-5">
                            <td className="px-4 py-2 border-b border-neutral-10">
                              {errorData.rowData.lineNumber}
                            </td>
                            {Object.keys(state.mappingFields)
                              .sort((a, b) =>
                                sortFieldsByConfig(a, b, state.fieldConfigs)
                              )
                              .map((fieldKey) => {
                                const fieldValue =
                                  errorData.rowData[
                                    fieldKey as keyof typeof errorData.rowData
                                  ] || "";
                                const hasError =
                                  errorData.errorMessages[fieldKey] !==
                                  undefined;

                                return (
                                  <td
                                    key={fieldKey}
                                    className="px-4 py-2 border-b border-neutral-10 relative whitespace-nowrap overflow-hidden text-ellipsis"
                                  >
                                    <div className="flex items-center">
                                      <span
                                        className={
                                          hasError ? "text-red-500" : ""
                                        }
                                      >
                                        {String(fieldValue)}
                                      </span>
                                      {hasError && (
                                        <div
                                          className="ml-2 text-red-light cursor-help"
                                          data-tip={
                                            errorData.errorMessages[fieldKey]
                                          }
                                        >
                                          <Icon.WarningCircle className="fill-red" />
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </CustomScrollbar>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-5">
        {state.importSuccess ? (
          <Common.Button onClick={handleBackToList}>Back to list</Common.Button>
        ) : (
          <>
            <Common.Button color="transparent" onClick={handleBackToList}>
              Cancel
            </Common.Button>
            <Common.Button onClick={handleRetry}>Retry</Common.Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportResult;
